'use client'

import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { 
  FiClock, 
  FiTerminal,
  FiCode,
  FiCopy,
  FiSend
} from 'react-icons/fi'
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import Swal from 'sweetalert2'
import { ALL_DOCUMENTATION } from '@/lib/facturacion-constants'

interface ApiPlaygroundProps {
  title: string;
  description: string;
  endpoint: string;
  method: 'GET' | 'POST';
  defaultBody?: any;
  showBody?: boolean;
  docType?: string;
}

export function ApiPlayground({ title, description, endpoint, method, defaultBody, showBody = true, docType }: ApiPlaygroundProps) {
  const [token, setToken] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [requestBody, setRequestBody] = useState(defaultBody ? JSON.stringify(defaultBody, null, 4) : '')
  const [response, setResponse] = useState<any>(null)
  const [responseTime, setResponseTime] = useState<string | null>(null)

  useEffect(() => {
    fetchToken()
  }, [])

  const fetchToken = async () => {
    try {
      const res = await fetch('/api/user/token')
      const data = await res.json()
      if (data.success) setToken(data.token)
    } catch (e) {
      console.error(e)
    }
  }

  const handleExecute = async () => {
    if (!token) return Swal.fire('Error', 'Ingresa un token', 'error')
    
    // Validar que la claveSecreta sea válida
    if (showBody && method === 'POST') {
      try {
        const parsedBody = JSON.parse(requestBody)
        const claveSecreta = parsedBody.claveSecreta
        
        if (!claveSecreta || claveSecreta.trim() === '') {
          return Swal.fire({
            icon: 'error',
            title: 'Clave Secreta Requerida',
            html: 'Debes incluir el campo <strong>claveSecreta</strong> en el JSON para identificar la empresa.',
            confirmButtonColor: '#3b82f6'
          })
        }
        
        if (claveSecreta === 'tu-clave-secreta-aqui' || claveSecreta === 'TU_CLAVE' || claveSecreta === 'TU_CLAVE_SECRETA') {
          return Swal.fire({
            icon: 'warning',
            title: 'Clave Secreta Inválida',
            html: 'Reemplaza <code>TU_CLAVE_SECRETA</code> con la <strong>clave secreta real</strong> de tu empresa.<br><br>Puedes encontrarla en <strong>Gestión de Empresas → Configurar</strong>.',
            confirmButtonColor: '#3b82f6'
          })
        }
      } catch (e) {
        return Swal.fire({
          icon: 'error',
          title: 'JSON Inválido',
          text: 'El JSON del payload tiene errores de sintaxis. Revísalo antes de continuar.',
          confirmButtonColor: '#3b82f6'
        })
      }
    }
    
    setLoading(true)
    setResponse(null)
    setResponseTime(null)

    const startTime = performance.now()

    try {
      const options: RequestInit = {
        method,
        headers: {
          'Authorization': `Bearer ${token.trim()}`,
          'Content-Type': 'application/json'
        }
      }

      if (method === 'POST' && showBody) {
        options.body = requestBody
      }

      const res = await fetch(endpoint, options)
      const data = await res.json()
      const endTime = performance.now()
      
      const timeSecs = (endTime - startTime) / 1000
      setResponseTime(timeSecs < 1 ? `${(timeSecs * 1000).toFixed(0)}ms` : `${timeSecs.toFixed(2)}s`)
      setResponse(data)
    } catch (e: any) {
      setResponse({ error: 'Error al conectar con la API', details: e.message })
    } finally {
      setLoading(false)
    }
  }

  const copyResponse = () => {
    if (!response) return
    navigator.clipboard.writeText(JSON.stringify(response, null, 2))
    Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Copiado', showConfirmButton: false, timer: 2000 })
  }

  const getDetailedDocs = () => {
    if (!docType || !ALL_DOCUMENTATION[docType]) return []
    const docSet = ALL_DOCUMENTATION[docType]
    
    try {
      const json = JSON.parse(requestBody)
      const result: { key: string; desc: string; type: string }[] = []
      
      const process = (obj: any, path = '') => {
        if (!obj || typeof obj !== 'object') return
        
        Object.keys(obj).forEach(key => {
          const fullPath = path ? `${path}.${key}` : key
          const value = obj[key]
          
          let typeStr: string = typeof value
          if (Array.isArray(value)) typeStr = 'Array'
          else if (value === null) typeStr = 'null'

          if (docSet[key]) {
            result.push({ key: fullPath, desc: docSet[key], type: typeStr })
          }

          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            process(value, fullPath)
          } else if (Array.isArray(value) && value.length > 0) {
            process(value[0], `${fullPath}[0]`)
          }
        })
      }
      
      process(json)
      return result
    } catch (e) {
      return []
    }
  }

  const detailedDocs = getDetailedDocs()

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        <p className="text-gray-500">{description}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-none shadow-sm flex flex-col h-full">
          <CardHeader className="bg-gray-50/50 border-b py-4 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-bold flex flex-col gap-1">
                <span>Configuración de Solicitud</span>
                <span className="text-[10px] font-normal text-gray-500 uppercase tracking-widest">
                  {method} | JSON | Bearer Token Auth
                </span>
            </CardTitle>
            
            {docType && ALL_DOCUMENTATION[docType] && (
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="rounded-xl border-blue-200 text-blue-600 hover:bg-blue-50 text-[10px] font-bold gap-2">
                    <FiCode /> Detalle Técnico
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col rounded-[2rem] border-none shadow-2xl">
                  <DialogHeader className="p-8 bg-blue-600 text-white flex-none">
                    <DialogTitle className="text-2xl font-black flex items-center gap-3">
                      <FiTerminal /> Diccionario Técnico ({title})
                    </DialogTitle>
                    <p className="text-blue-100 text-sm mt-1">Explicación detallada de cada campo del JSON.</p>
                  </DialogHeader>
                  <div className="flex-grow overflow-y-auto p-8 custom-scrollbar bg-gray-50">
                    <div className="grid grid-cols-1 gap-4">
                      {detailedDocs.length > 0 ? (
                        detailedDocs.map((doc, i) => (
                          <div key={i} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all">
                            <div className="flex items-center justify-between mb-2">
                              <code className="text-blue-600 font-black text-sm">{doc.key}</code>
                              <span className="px-3 py-1 bg-gray-100 text-gray-500 rounded-full text-[10px] font-bold uppercase tracking-wider">
                                {doc.type}
                              </span>
                            </div>
                            <p className="text-gray-600 text-sm leading-relaxed">{doc.desc}</p>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-10 text-gray-400">
                          No se encontraron descripciones para los campos del JSON actual.
                        </div>
                      )}
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </CardHeader>
          <CardContent className="space-y-6 pt-6 flex-1 flex flex-col">
            <div className="space-y-4 flex-1 flex flex-col">
                <div className="space-y-2">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Endpoint</label>
                    <input 
                        type="text" 
                        readOnly 
                        value={endpoint}
                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-mono text-gray-500"
                    />
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Bearer Token</label>
                  <input 
                      type="text" 
                      value={token}
                      onChange={(e) => setToken(e.target.value)}
                      placeholder="Ingresa tu token"
                      className="w-full px-4 py-2 bg-white border border-gray-200 rounded-xl text-xs font-mono focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>

                {showBody && method === 'POST' && (
                  <div className="space-y-2 flex-1 flex flex-col">
                      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Cuerpo de la Solicitud (JSON)</label>
                      <textarea 
                          value={requestBody}
                          onChange={(e) => setRequestBody(e.target.value)}
                          className="flex-1 w-full px-4 py-4 bg-slate-900 text-green-400 font-mono text-xs rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none min-h-[300px]"
                      />
                  </div>
                )}

                <Button 
                    onClick={handleExecute}
                    disabled={loading}
                    className="w-full bg-blue-600 hover:bg-blue-700 py-6 rounded-xl font-bold shadow-lg shadow-blue-100 gap-2"
                >
                    {loading ? <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><FiSend /> Probar API</>}
                </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm min-h-[500px] flex flex-col overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between border-b py-4 bg-gray-50/50">
            <CardTitle className="text-sm font-bold flex items-center gap-2 text-gray-700">
              <FiCode className="text-purple-500" /> Respuesta JSON
            </CardTitle>
            <div className="flex items-center gap-2">
              {responseTime && <span className="text-[10px] font-bold text-gray-400 bg-white px-2 py-1 rounded-md border">{responseTime}</span>}
              <button onClick={copyResponse} className="p-2 text-gray-400 hover:text-blue-600"><FiCopy /></button>
            </div>
          </CardHeader>
          <CardContent className="flex-1 p-0 overflow-hidden relative bg-[#1e1e1e]">
             <div className="absolute top-0 left-0 w-full h-full p-6 font-mono text-sm overflow-auto text-green-400 scrollbar-thin scrollbar-thumb-gray-800 scrollbar-track-transparent">
                {response ? <pre className="whitespace-pre-wrap">{JSON.stringify(response, null, 2)}</pre> : <div className="h-full flex flex-col items-center justify-center text-gray-600">Aquí aparecerá la respuesta...</div>}
             </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
